import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import {
  classifyPathwayForSchoolUser,
  formatLongDate,
  isSecondaryPupil,
  isStoppedPathwayState,
  letterSentAt,
  letterSharedWithRole,
  referralStageFor,
  viewLetterLabel,
} from '../../utils/harbourPupils';
import {
  PATHWAY_LABELS,
  pathwayReviews,
  pathwayTriage,
} from '../../utils/triagePathways';
import { FAMILY_SERVICE_NAME, familyUserName, guardianConsentState, referralConcernWords } from '../../utils/familyPortal';
import FamilyWithdrawConsent, { withdrawModeFor } from './FamilyWithdrawConsent';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyCrisisLine from './FamilyCrisisLine';
import FamilyProfileSharing from './FamilyProfileSharing';
import useIsDesktop from './useIsDesktop';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import '../../components/harbour/harbour.css';
import './familyReferral.css';

/*
 * Family portal · follow a referral (TDI-53, parent-or-carer side).
 * One page per triage pathway (ADHD and autism are separate pages); a vertical
 * timeline shows where the referral is, with the current row carrying the one
 * status chip. One state is visible at a time, chosen from the live record.
 *
 * Mobile only: the desktop designs fold the referral states into the Your
 * child record flyout (stage strips and the letter action live there), so on
 * desktop widths this page redirects to the child's record.
 *
 * CONTENT OWNERSHIP: the team contact details below are Blackpool / Lancashire
 * specific, as written in the design. They are correct for the only
 * organisation currently using RMS; before a second organisation is onboarded
 * they must move to per-organisation configuration (same position as
 * src/rms/utils/safetySignposting.js). The crisis wording lives in the shared
 * FamilyCrisisLine component.
 */
const TEAM_EMAIL = 'bfwh.options4cyp@nhs.net';
const TEAM_PHONE_LABEL = '0800 121 7762 (Option 5)';
const TEAM_PHONE_HREF = 'tel:08001217762';

const GUARDIAN_USER_TYPE = 'RMS Legal Guardian';

/** Referral stages in timeline order, with the designed stage names.
 * Four stages (TDI-826 part 4, T-17): a referral whose letter has been sent
 * reads "Outcome available" whichever way the decision went — a fifth
 * "Closed" step made every referral look like something more was coming. */
const REFERRAL_TIMELINE = [
  { key: 'inProgress', name: 'In progress' },
  { key: 'sentWaitingForReview', name: 'Sent, waiting for review' },
  { key: 'inReview', name: 'In review' },
  { key: 'outcomeAvailable', name: 'Outcome available' },
];

/**
 * Is this pathway's outcome letter shareable with the signed-in parent or
 * carer? Mirrors the proven gating in ServiceUserPatientOverview (TD-145):
 * the PDF must exist and a clinician must have sent the letter to the
 * guardian role. Nothing on these pages may imply a letter exists earlier.
 */
export function letterSharedWithGuardian(patient, pathwayKey) {
  return letterSharedWithRole(patient, pathwayKey, 'guardian');
}

/**
 * The visible referral state for this pathway, from the live record:
 * - a stopped referral pathway wins (consent withdrawn / no consent);
 * - otherwise the same classification the school view uses, per pathway.
 * `allFormsIn` is deliberately false here: since the readiness flag became the
 * server signal for "last form submitted" the record carries it directly, and
 * this page does not load the form instances the legacy fallback would need.
 */
export function familyReferralState(patient, pathwayKey) {
  // The stop used to be decided here and nowhere else, which is why the
  // school side never showed one (TDI-474). It now lives inside the shared
  // classifier, so both sides say the same thing about the same record.
  return classifyPathwayForSchoolUser({
    reviews: pathwayReviews(patient, pathwayKey),
    triageStatus: pathwayTriage(patient, pathwayKey)?.status || null,
    readyForTriage: patient?.triageReadiness?.status === 'ready',
    allFormsIn: false,
    letterShared: letterSharedWithRole(patient, pathwayKey, 'guardian'),
    stopped: patient?.referralPathway?.status === 'stopped',
    stopReason: patient?.referralPathway?.stopReason || null,
  });
}

/** The page head shared with the letter page: eyebrow, title, optional meta. */
export function FamilyPathwayPageHead({ title, meta }) {
  return (
    <div className="family-path-head">
      <div className="family-path-eyebrow harbour-body-16-semibold">
        <HarbourPathwayMark pathway="referral" />
        Referral pathway
      </div>
      <h1 className="harbour-heading-24 family-path-title">{title}</h1>
      {meta && <p className="harbour-body-16 family-path-meta">{meta}</p>}
    </div>
  );
}

/** "If you have questions, contact the team" with 48px email and phone rows. */
export function FamilyContactTeam() {
  return (
    <div className="family-contact">
      <p className="harbour-body-16 family-contact__lead">If you have questions, contact the team</p>
      <div className="family-contact__row">
        <HarbourIcon name="email" size={24} />
        <a className="harbour-body-16 family-contact__link" href={`mailto:${TEAM_EMAIL}`}>
          {TEAM_EMAIL}
        </a>
      </div>
      <div className="family-contact__row">
        <HarbourIcon name="phone" size={24} />
        <a className="harbour-body-16 family-contact__link" href={TEAM_PHONE_HREF}>
          {TEAM_PHONE_LABEL}
        </a>
      </div>
    </div>
  );
}

/** Record-level loading skeleton and could-not-load state (E2 vocabulary). */
export function FamilyRecordStates({ loading, title, onRetry }) {
  if (loading) {
    return (
      <div className="harbour-skeleton-bars animate-pulse" aria-hidden="true">
        <div className="harbour-skeleton" />
        <div className="harbour-skeleton" />
        <div className="harbour-skeleton" />
      </div>
    );
  }
  return (
    <div className="family-page-error">
      {/* These are read-only pages, so the old line about entered answers not
          being lost answered a worry nobody had here. */}
      <HarbourNotice kind="error" title={title}>
        Check your connection, then try again.
      </HarbourNotice>
      <HarbourButton kind="secondary" onClick={onRetry}>Try again</HarbourButton>
    </div>
  );
}

/**
 * Vertical stage timeline (Harbour/Stage track · Mobile, Timeline layout).
 * The current row carries the status chip and is the one status line; done
 * names stay ink, upcoming names are muted. A stopped referral collapses to
 * the single stop row (the stop node and the stopped chip).
 */
function ReferralTimeline({ state }) {
  if (isStoppedPathwayState(state)) {
    return (
      <ol className="family-timeline" aria-label="Referral stages">
        <li className="family-timeline__row" aria-current="step">
          <span className="family-timeline__rail" aria-hidden="true">
            <span className="family-timeline__line" />
            <span className="family-timeline__node family-timeline__node--stopped" />
            <span className="family-timeline__line" />
          </span>
          <HarbourStatusChip status={state} />
        </li>
      </ol>
    );
  }

  // 'closed' sits on the Outcome available step, as the school strip has it.
  const timelineState = referralStageFor(state);
  const currentIndex = Math.max(0, REFERRAL_TIMELINE.findIndex((s) => s.key === timelineState));
  const lastIndex = REFERRAL_TIMELINE.length - 1;
  const lineClass = (strong) =>
    `family-timeline__line ${strong ? 'family-timeline__line--strong' : 'family-timeline__line--soft'}`;
  return (
    <ol className="family-timeline" aria-label="Referral stages">
      {REFERRAL_TIMELINE.map((stage, i) => {
        const phase = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
        return (
          <li
            key={stage.key}
            className="family-timeline__row"
            aria-current={phase === 'current' ? 'step' : undefined}
          >
            <span className="family-timeline__rail" aria-hidden="true">
              {i === 0 ? <span className="family-timeline__line" /> : lineAbove(i, currentIndex, lineClass)}
              <span className={`family-timeline__node family-timeline__node--${phase}`} />
              {i === lastIndex ? <span className="family-timeline__line" /> : lineBelow(i, currentIndex, lineClass)}
            </span>
            {phase === 'current' ? (
              <HarbourStatusChip status={stage.key} />
            ) : (
              <span
                className={`harbour-body-16 family-timeline__name ${
                  phase === 'upcoming' ? 'family-timeline__name--upcoming' : ''
                }`.trim()}
              >
                {stage.name}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function lineAbove(i, currentIndex, lineClass) {
  return <span className={lineClass(i <= currentIndex)} />;
}
function lineBelow(i, currentIndex, lineClass) {
  return <span className={lineClass(i < currentIndex)} />;
}

export default function FamilyReferralPage() {
  const navigate = useNavigate();
  const { patientRecordId, pathwayKey } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  // This page is the parent-or-carer view only. Other signed-in RMS users land
  // on their own dashboard instead.
  if (claims && claims.userType !== GUARDIAN_USER_TYPE) {
    return <Navigate to="/rms/dashboard" replace />;
  }

  // Desktop folds the referral states into the Your child record flyout: the
  // stage strips and the letter action live there, so this page is mobile-only.
  if (isDesktop) {
    return <Navigate to={`/rms/family/child/${patientRecordId}`} replace state={{ stay: true }} />;
  }

  const pupil = pupils.find((p) => p.id === patientRecordId) || null;
  const patient = pupil?.patient || {};
  const userName = familyUserName(user, pupils) || 'Account';
  const back = { to: `/rms/family/child/${patientRecordId}`, label: 'Back' };
  const pathwayLabel = PATHWAY_LABELS[pathwayKey] || '';

  if (loading || error || !pupil || !pathwayLabel) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <FamilyRecordStates
          loading={loading && !error}
          title="This referral could not be loaded"
          onRetry={() => reload({ force: true })}
        />
      </FamilyPortalShell>
    );
  }

  const childName =
    patient.subject_first_name || (pupil.name || '').split(' ')[0] || 'your child';
  const state = familyReferralState(patient, pathwayKey);
  const letterReady =
    (state === 'outcomeAvailable' || state === 'closed') &&
    letterSharedWithGuardian(patient, pathwayKey);
  // When this referral's letter was sent (TDI-691); none for a letter sent
  // before the date was recorded.
  const letterSentOn = letterReady ? formatLongDate(letterSentAt(patient, pathwayKey)) : null;
  // Taking back permission (TDI-473). Withdrawal is for the whole referral,
  // so a combined referral names both concerns even on one pathway's page.
  const withdrawMode = withdrawModeFor({
    stopped: state.startsWith('stopped'),
    consent: guardianConsentState(patient.referralPathway),
    pathwayStates: [state],
  });
  const withdrawUi = withdrawMode && (
    <FamilyWithdrawConsent
      mode={withdrawMode}
      orgId={orgId}
      patientRecordId={patientRecordId}
      childName={childName}
      concernWords={referralConcernWords(patient.intakeConcern)}
      pathways={[pathwayKey]}
      isDesktop={isDesktop}
      onWithdrawn={() => reload({ silent: true })}
    />
  );

  return (
    <FamilyPortalShell userName={userName} back={back}>
      <div className="family-path-stack">
        <FamilyPathwayPageHead title={`${pathwayLabel} referral`} />

        <div className="family-referral-card">
          <ReferralTimeline state={state} />

          {state === 'sentWaitingForReview' && (
            <p className="harbour-body-16">
              {/* Receipt sentence verbatim from the design, with the real
                  organisation and child (recognition is what makes it credible). */}
              {FAMILY_SERVICE_NAME} has received {childName}&rsquo;s referral.
            </p>
          )}

          {state === 'stoppedConsentWithdrawnByFamily' && (
            <HarbourNotice kind="info" title="You can change your mind at any time by telling the school." />
          )}

          {/* The family's profile sharing answer, changeable until the
              outcome letter (TDI-482, C-1 to C-5), then the withdraw entry
              (TDI-473, P-1): the row above the tertiary link, as C-1 draws
              them. */}
          <FamilyProfileSharing
            patient={patient}
            state={state}
            orgId={orgId}
            patientRecordId={patientRecordId}
            childName={childName}
            concernLabel={`${pathwayLabel} referral`}
            isDesktop={false}
            isSecondary={isSecondaryPupil(pupil)}
            onSaved={() => reload({ silent: true })}
          />
          {withdrawMode === 'entry' && withdrawUi}

          {letterReady && (
            <div className="family-letter-cta">
              <p className="harbour-body-16-bold family-letter-cta__label">{`${pathwayLabel} referral letter`}</p>
              {letterSentOn && <p className="harbour-body-16">{`Letter sent ${letterSentOn}`}</p>}
              <HarbourButton
                kind={state === 'outcomeAvailable' ? 'family' : 'secondary'}
                trailingIcon="arrow-right"
                className="family-btn-block"
                onClick={() => navigate(`/rms/family/child/${patientRecordId}/letter/${pathwayKey}`)}
              >
                {viewLetterLabel(pathwayKey)}
              </HarbourButton>
            </div>
          )}

          {/* Past the outcome letter the contact block carries the
              take-back-your-permission line too (P-10), so it is not
              shown twice. */}
          {withdrawMode === 'notice' ? withdrawUi : <FamilyContactTeam />}
        </div>

        <FamilyCrisisLine childName={childName} />
      </div>
    </FamilyPortalShell>
  );
}
